import * as Sentry from '@sentry/react';
import {
  browserLocalPersistence,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  User,
  UserCredential,
} from 'firebase/auth';
import { createContext, useContext, useEffect, useState } from 'react';
import { profileService } from 'src/db/profile.service';
import { userDocService } from 'src/db/userDoc.service';
import {
  Profile,
  ProfileDoc,
  UserDoc,
} from 'src/workspace/workspace.interfaces';
import Stripe from 'stripe';
import { auth } from '../firebase';

interface AuthContextType {
  user: User | null;
  userDoc: UserDoc | null;
  profileDoc: ProfileDoc | null;
  subscription: Stripe.Subscription | null;
  isAdmin: boolean;
  loaded: boolean;
  login: (email: string, password: string) => Promise<UserCredential | null>;
  signup: (
    email: string,
    password: string,
    termsVersion: number,
  ) => Promise<UserCredential | null>;
  logout: () => Promise<void>;
  refresh: () => Promise<Profile | null>;
}
export const AuthContext = createContext<AuthContextType>({
  user: null,
  userDoc: null,
  profileDoc: null,
  subscription: null,
  isAdmin: false,
  loaded: false,
  login: async () => null,
  signup: async () => null,
  logout: async () => {},
  refresh: async () => null,
});

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }: any) {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [currentUserDoc, setCurrentUserDoc] = useState<UserDoc | null>(null);
  const [currentProfile, setCurrentProfile] = useState<ProfileDoc | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  // const [loading, setLoading] = useState(true);
  const [loaded, setLoaded] = useState(false);
  const [subscription, setSubscription] = useState<Stripe.Subscription | null>(
    null,
  );
  useEffect(() => {
    (async () => {
      try {
        await auth.setPersistence(browserLocalPersistence);
        auth.onAuthStateChanged(async (user) => {
          if (user) {
            Sentry.setUser({ id: user.uid, email: user.email ?? undefined });
            setCurrentUser(user);
            const userDoc = await userDocService.getUserDoc(user.uid);
            setCurrentUserDoc(userDoc);
            const profile = await profileService.getProfile(user.uid);
            setCurrentProfile(profile);
            const subscriptions = await userDocService.getUserSubscriptions(
              user.uid,
            );
            await checkAdmin(user);

            if (subscriptions.length) {
              subscriptions.sort((a, b) => b.created - a.created);
              setSubscription(subscriptions[0]!);
            }
          }
          // setLoading(false);
          setLoaded(true);
        });
      } catch (error) {
        console.error('Error in auth state change:', error);
        // setLoading(false);
        setLoaded(true);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const logout = async () => {
    Sentry.setUser(null);
    setCurrentUser(null);
    await signOut(auth);
  };

  const login = async (email: string, password: string) =>
    await signInWithEmailAndPassword(auth, email, password);

  const signup = async (
    email: string,
    password: string,
    termsVersion: number,
  ) => {
    const userCredential = await createUserWithEmailAndPassword(
      auth,
      email,
      password,
    );
    await userDocService.createUser(userCredential.user, termsVersion);
    await profileService.createProfile(userCredential.user.uid, {});
    return userCredential;
  };

  const checkAdmin = async (user: User) => {
    const token = await user.getIdTokenResult(true);
    const isAdmin = token?.claims.role === 'admin';
    setIsAdmin(isAdmin);
  };

  const refresh = async () => {
    try {
      if (currentUser) {
        const userDoc = await userDocService.getUserDoc(currentUser.uid);
        const profile = await profileService.getProfile(currentUser.uid);
        await checkAdmin(currentUser);
        setCurrentUserDoc(userDoc);
        setCurrentProfile(profile);
      }
      return null;
    } catch (error) {
      console.error('Error refreshing profile:', error);
      return null;
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user: currentUser,
        userDoc: currentUserDoc,
        profileDoc: currentProfile,
        subscription,
        isAdmin,
        loaded,
        login,
        logout,
        signup,
        refresh,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}
